import { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const TYPE_DOT = {
  reminder: "bg-amber-500",
  rejected: "bg-rose-500",
  signed: "bg-blue-500",
  incoming: "bg-emerald-500",
  comment: "bg-slate-400",
  info: "bg-slate-400",
};

function fmtTime(ts) {
  return ts ? ts.slice(0, 16).replace("T", " ") : "";
}

export function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread_count);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const handleOpen = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button className="relative text-slate-400 hover:text-white" data-testid="notifications-bell">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] leading-none rounded-full w-4 h-4 flex items-center justify-center" data-testid="notifications-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-popover">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Уведомления</div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="px-4 py-8 text-center text-xs text-slate-400">Пока нет уведомлений</div>}
          {items.map((n) => (
            <div key={n.id} className="px-4 py-3 border-b border-slate-50 text-sm" data-testid={`notification-item-${n.id}`}>
              <div className="flex items-start gap-2">
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TYPE_DOT[n.type] || TYPE_DOT.info}`} />
                <div>
                  <div className="text-slate-800">{n.message}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{fmtTime(n.created_at)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
